<!-- Component used to edit the markdown content of a note -->
<template>
    <div class="editor-container" :class="{'disabled': disabled}">
        <portal to="note-header" v-if="!disabled">
            <div id="editor-toolbar">
                <i class="fas fa-italic toolbar-item" @click="makeItalic"></i>
                <i class="fas fa-bold toolbar-item" @click="makeBold"></i>
                <i class="fas fa-strikethrough toolbar-item" @click="makeStrikeThrough"></i>
                <i class="fas fas fa-heading toolbar-item" @click="makeHeading"></i>
                <i class="fas fas fa-indent fa-flip-horizontal toolbar-item" @click="unIndent"></i>
                <i class="fas fas fa-indent toolbar-item" @click="indent"></i>
            </div>
        </portal>

        <textarea ref="textarea"
            id="editor"
            class="scroll"
            v-model="noteText"
            autofocus
            @focus="onFocus"
            @blur="onBlur"
            @keydown.tab.stop.prevent="indent"
            :disabled="disabled"
        />

        <div id="editor-statusbar">
            Line {{current_line_number}} Col {{current_column_number}}
        </div>
    </div>
</template>

<script>
// Had to import lodash to make it work.
// Is there a global way I can do this?
import _ from 'lodash';

const FONT_STYLES = {
    bold         : ["**"  , "**"   ],
    italic       : ["*"   , "*"    ],
    strikeThrough: ["~~"  , "~~"   ],
    heading      : ["<h1>", "</h1>"],
}

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

function getIndexOfWordAt(str, pos) {
    // Perform type conversions.
    str = String(str);
    pos = Number(pos) >>> 0;


    if ([str[pos], str[pos-1]].includes(' ')) return {
        start: pos,
        end: pos,
        word: '',
        length: 0
    }

    // Search for the word's beginning and end.
    let left = str.slice(0, pos+1).search(/\S+$/),
        right = str.slice(pos).search(/\s/);

    let word;
    // The last word in the string is a special case.
    if (right < 0) {
        word=str.slice(left);
        right = left + word.length;
    }

    // Spaces are also a special case;
    if (left < 0) {
        word=str.slice(right);

    }

    const start = left;
    const end = pos+right;

    word = str.slice(start, end)
    // Return the word, using the located bounds to extract it from the string.
    return {
        start,
        end,
        word,
        length: word.length
    };

}

const tab_character = "    ";

const debouncedUpdateCursor = _.debounce(function (event) {
    this.updateCursor();
}, 200);

export default {
    props: [
        'note',
        'disabled',
    ],

    data(){
        return{
            markdown: "",
            cursor_pos: {
                line: 1,
                column: 0
            },
            focused: false,
            cursor_pos_interval: null,
        }
    },

    methods:{
        updateCursor() {
            let textarea = this.$refs.textarea;

            if (this.focused || textarea === document.activeElement) {
                let pos = this.getLineNumberAndColumnIndex(textarea);

                this.$nextTick(() => {
                    this.cursor_pos = {
                        line: pos.line,
                        column: pos.column
                    }
                });
            }
        },
        onFocus(event) {
            this.$emit('focus', event);
            this.focused = true;

            if (!this.cursor_pos_interval) this.cursor_pos_interval = setInterval(this.updateCursor.bind(this), 200);
        },

        onBlur(event) {
            this.$emit('blur', event);
            this.focused = false;
            if (this.cursor_pos_interval) clearInterval(this.cursor_pos_interval);
        },

        appendSymbols(string, frontSymbol, backSymbol) {
            return `${frontSymbol}${string}${backSymbol}`;

        },

        wrapWithFormatting(style) {
            let textarea = this.$refs.textarea;
            let wholeText = this.noteText;

            let frontSymbol = FONT_STYLES[style][0];
            let backSymbol  = FONT_STYLES[style][1];
            let frontChars  = frontSymbol.length;
            let backChars   = backSymbol.length;

            let start = this.$refs.textarea.selectionStart;
            let end   = this.$refs.textarea.selectionEnd;

            let new_cursor_pos = frontChars + end + backChars + 1;

            // If no text is selected, select the word the carret is on.
            if (start === end) {
                const word_pos = getIndexOfWordAt(wholeText, start)
                start = word_pos.start;
                end   = word_pos.end;
                if (word_pos.length == 0) new_cursor_pos = frontChars + start;
            }

            let beforestart = start-frontChars;
            if (beforestart < 0) beforestart = 0;

            let afterend = frontChars + end + backChars;
            if (afterend > wholeText.length) afterend = wholeText.length;

            // get the selected text, plus an amount of characters
            // equivalent to the length of the style, on each end
            let full_segment = wholeText.substring(beforestart, afterend);

            // get the selected text.
            let segment = wholeText.substring(start,end);

            let frontEsymbol = escapeRegExp(frontSymbol);
            let backEsymbol  = escapeRegExp(backSymbol);
            let regex = new RegExp(`${frontEsymbol }.*${backEsymbol}`, 'g')
            let formatted_segment = segment;

            //If the segment already matches a string with this style, remove the style instead
            let match = segment.match(regex);
            if (match ) {
                if (match[0] === segment) {
                    formatted_segment = segment.slice(frontChars, segment.length - backChars);
                    new_cursor_pos = end - (frontChars + backChars);
                }
            } else {
                //If the segment really is unformatted text, format it:
                formatted_segment = this.appendSymbols(segment, frontSymbol, backSymbol);
                if (segment.length > 0) new_cursor_pos = start + formatted_segment.length
            }

            // the segment didn't match, but we expanded the search and got a match, remove the style
            if ( formatted_segment === full_segment) {
                start = beforestart;
                end = afterend;
                formatted_segment = segment;
                new_cursor_pos = afterend - backChars;
            }

            this.saveNote(
                wholeText.substring(0,start) +
                formatted_segment +
                wholeText.substring(end)
            ).then(() => {
                this.$nextTick(() => {
                    this.$refs.textarea.focus();
                    this.$refs.textarea.selectionStart = new_cursor_pos;
                    this.$refs.textarea.selectionEnd = new_cursor_pos;
                })
            });
        },

        unIndent() {
            let textarea = this.$refs.textarea;
            let value = textarea.value;

            let cursor_pos = this.getLineNumberAndColumnIndex(textarea);
            let start_pos = textarea.selectionStart;
            let end_pos = textarea.selectionEnd;

            let lines = value.split('\n');
            let cur_line = cursor_pos.line-1;
            let line = lines[cur_line];

            let extracted_value = line.slice(0,tab_character.length);
            if (extracted_value === tab_character) {
                line = line.slice(tab_character.length);
                lines[cur_line] = line;

                this.saveNote(lines.join('\n')).then(() => {
                    textarea.selectionStart = start_pos - tab_character.length;
                    textarea.selectionEnd = start_pos - tab_character.length;
                    this.updateCursor()
                });
            }
        },

        indent(event){
            // If shift is held, unindent instead:
            if (event &&
                event.type === "keydown"
                && event.getModifierState("Shift")
            )  {
                return this.unIndent();
            }

            let textarea = this.$refs.textarea;
            let value = textarea.value;

            let cursor_pos = this.getLineNumberAndColumnIndex(textarea);
            let start_pos = textarea.selectionStart;
            let end_pos = textarea.selectionEnd;

            let lines = value.split('\n');
            let cur_line = cursor_pos.line-1;
            let line = lines[cur_line];

            line = tab_character + line;
            lines[cur_line] = line;

            this.saveNote(lines.join('\n')).then(() => {
                textarea.selectionStart = start_pos + tab_character.length;
                textarea.selectionEnd = start_pos + tab_character.length;
                this.updateCursor()
            });
        },

        makeItalic(){
            this.wrapWithFormatting('italic');
        },

        makeBold(){
            this.wrapWithFormatting('bold');
        },

        makeStrikeThrough(){
            this.wrapWithFormatting('strikeThrough');
        },

        makeHeading(){
            this.wrapWithFormatting('heading');
        },


        saveNote(val) {
            return this.$store.dispatch('updateNote',{
                ...this.note,
                text: val
            });
        },

        getLineNumberAndColumnIndex(textarea){
            var textLines = textarea.value.substr(0, textarea.selectionStart).split("\n");

            var currentLineNumber = textLines.length;
            var currentColumnIndex = textLines[textLines.length-1].length;

            return {
                line: currentLineNumber,
                column: currentColumnIndex
            }
        },

        // I haven't yet found a way to make this run AFTER the keydown
        //  event is handled by the browser, so just debounce it with a delay:
        updateCursorPos: debouncedUpdateCursor.bind(this),
    },

    computed:{
        // note(){
        //     return this.$store.getters.current_note;
        // },

        noteText:{
            get: function () {
                return this.note?.text || null
            },
            set: function (val) {
                return this.saveNote(val);
            }
        },

        current_line_number() {
            return this.cursor_pos.line;
        },

        current_column_number() {
            return this.cursor_pos.column;
        },
    },

    watch: {
    }
}
</script>

<style lang="scss">
    @import "../assets/colors.sass";

    .editor-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        width: calc(100% -10px);
        height: 100%;
        // margin-right: 10px;
        position: relative;
        &::after {
            pointer-events: none;
            $margin: 15px;
            border: 1px dashed $border-medium;
            content: " ";
            
            width: calc(100% - #{$margin} * 2);
            height: calc(100% - calc(#{$margin} * 3));
            position: absolute;
            top: calc(#{$margin} + 5px);
            left: $margin;
            display: block;
        }

        &.disabled {
            pointer-events: none;
        }
    }

    #editor {
        margin: 0;
        resize: none;
        width:100%;
        color:#f2f2f3;
        flex: 1;
        padding: 27px 25px;
        padding-bottom: 0;
        background-color: #2d2d2d;
        font-family: Roboto Mono;
        font-size: 15px;
        border: none;
        border-right: 1px solid $border-medium;
        border-bottom: 1px solid $border-medium;

       
        // box-sizing: border-box;
    }
    #editor-toolbar {
        display: flex;
        flex-direction: row;
        align-items: center;
        width:100%;
        position: relative;
        z-index: 1;
        flex: 1;
    }

    #editor-statusbar {
        display: flex;
        width: 100%;
        font-size: 0.8em;
        height: 25px;
        justify-content: flex-end;
        align-items: center;
        padding: 0px 5px;
        position: absolute;
        bottom: 0;
        right: 0;
    }

    .toolbar-item {
        transition: background-color 0.2s ease-in;
        font-size: 15px;
        padding: 5px;
    }

    .toolbar-item:hover {
        background-color: #3d3d3d;
    }
</style>